import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  Mutation,
  ProductMutationUpdateArgs,
  ProductUpdateFailure,
  ProductWarehouseInput,
} from "@/graphql/codegen/graphql"
import { UpdateProductMutationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateProductWarehousesProps {
  productId: number
  version?: number
}

interface UseUpdateProductWarehousesReturn {
  updateProductWarehouses: (warehouseAreas: ProductWarehouseInput[], isEdit: boolean) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateProductWarehouses = ({
  productId,
  version,
}: UseUpdateProductWarehousesProps): UseUpdateProductWarehousesReturn => {
  const { enqueueNotification } = useNotificationsStore()

  const [{ fetching }, updateProduct] = useMutation<Pick<Mutation, "product">, ProductMutationUpdateArgs>(
    UpdateProductMutationDocument
  )

  const updateProductWarehouses = useCallback(
    async (warehouseAreas: ProductWarehouseInput[], isEdit: boolean): Promise<boolean> => {
      const { error, data } = await updateProduct({
        input: {
          productId,
          versionNumber: version,
          warehouses: warehouseAreas,
        },
      })
      const output = data?.product.update
      const hasError = error || output?.__typename === "ProductUpdateFailure"

      if (hasError) {
        enqueueNotification({
          type: NotificationType.Error,
          message: error?.message ?? (output as ProductUpdateFailure).error.message,
        })
        return false
      }

      enqueueNotification({
        type: NotificationType.Success,
        message: `Product warehouse details ${isEdit ? "updated" : "added"}`,
      })
      return true
    },
    [enqueueNotification, productId, updateProduct, version]
  )

  return { updateProductWarehouses, isUpdating: fetching }
}
